import axios from 'axios'

export interface PermitType {
  permitType: string
  permitCode: string
}

export interface TemplateFieldOption {
  optionId: string
  value: string
  label: string
  orderIndex: number
  isActive: boolean
}

export interface TemplateFieldDatasourceDisplayColumn {
  key?: string
  field: string
  label: string
}

export interface TemplateFieldDatasourceItem {
  value: string
  label: string
  columns?: Record<string, string | null>
}

export interface TemplateFieldDatasource {
  id: string
  sourceType: string
  tableName?: string
  valueColumn: string
  labelColumn: string
  whereSql?: string
  orderBySql?: string
  paramsJson?: string
  uiConfigJson?: string
  isActive: boolean
  searchableColumns?: string[]
  displayColumns?: TemplateFieldDatasourceDisplayColumn[]
  items: TemplateFieldDatasourceItem[]
}

export interface TemplateField {
  fieldId: string
  code: string
  label: string
  fieldType: string
  required: boolean
  visible?: boolean | null
  layout?: number | null
  orderIndex: number
  config?: string | null
  validation?: string | null
  visibilityRules?: string | null
  isKey: boolean
  sourceType?: string | null
  sourceRef?: string | null
  sourceParams?: string | null
  options: TemplateFieldOption[]
  datasource?: TemplateFieldDatasource | null
}

export interface TemplateSection {
  templateSectionId: string
  code: string
  title: string
  orderIndex: number
  isVisible?: boolean
  approvalStageId?: string | null
  visibilityRules?: string | null
  fields: TemplateField[]
}

export interface TemplateSummary {
  templateId: string
  templateVersionId: string
  permitCode: string
  templateName: string
  description?: string | null
  version: number
  status: string
  isTemplateActive: boolean
  createdAt: string
}

export interface TemplateRequiredDocument {
  requiredDocId: string
  documentId?: string | null
  documentName?: string | null
  isMandatory: boolean
  notes?: string | null
}

export interface TemplateSubactivityField {
  subactivityFieldId: string
  code: string
  label: string
  fieldType: string
  required: boolean
  orderIndex: number
  config?: string | null
  optionsJson?: string | null
  validationRegex?: string | null
}

export interface TemplateSubactivity {
  subactivityId: string
  code: string
  name: string
  description?: string | null
  orderIndex: number
  isActive: boolean
  fields: TemplateSubactivityField[]
  requiredDocuments: TemplateRequiredDocument[]
}

export interface TemplateConfiguration {
  templateId: string
  templateVersionId: string
  templateName: string
  permitType: string
  permitCode: string
  version: number
  generalEnabled?: boolean
  subactivityEnabled?: boolean
  workerEnabled?: boolean
  mapEnabled?: boolean
  ramEnabled?: boolean
  attachmentEnabled?: boolean
  sections: TemplateSection[]
  requiredDocuments: TemplateRequiredDocument[]
  subActivities: TemplateSubactivity[]
}

export interface UpdateTemplateFieldOptionInput {
  optionId?: string
  value: string
  label: string
  orderIndex: number
  isActive: boolean
}

export interface UpdateTemplateFieldDatasourceInput {
  datasourceId?: string | null
  sourceType: string
  tableName?: string | null
  valueColumn: string
  labelColumn: string
  whereSql?: string | null
  orderBySql?: string | null
  paramsJson?: string | null
  uiConfigJson?: string | null
  isActive?: boolean
}

export interface UpdateTemplateFieldInput {
  fieldId?: string
  code: string
  label: string
  fieldType: string
  required: boolean
  isKey: boolean
  orderIndex: number
  config?: string | null
  options?: UpdateTemplateFieldOptionInput[]
  datasource?: UpdateTemplateFieldDatasourceInput | null
}

export interface UpdateTemplateSectionInput {
  templateSectionId: string
  isVisible?: boolean
  fields: UpdateTemplateFieldInput[]
}

export interface UpdateTemplateSubactivityFieldInput {
  subactivityFieldId?: string
  code: string
  label: string
  fieldType: string
  required: boolean
  orderIndex: number
  config?: string | null
  optionsJson?: string | null
  validationRegex?: string | null
}

export interface UpdateTemplateSubactivityInput {
  subactivityId: string
  fields: UpdateTemplateSubactivityFieldInput[]
}

export interface UpdateTemplateConfigurationInput {
  tenantId: string
  templateId: string
  templateVersionId: string
  permitCode: string
  generalEnabled?: boolean
  subactivityEnabled?: boolean
  workerEnabled?: boolean
  mapEnabled?: boolean
  ramEnabled?: boolean
  attachmentEnabled?: boolean
  sections: UpdateTemplateSectionInput[]
  subActivities: UpdateTemplateSubactivityInput[]
}

export interface CreateTemplateInput {
  tenantId: string
  permitCode: string
  templateName: string
  description?: string | null
  copyFromTemplateVersionId?: string | null
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const buildUrl = (path: string) => {
  if (API_BASE_URL) {
    return `${API_BASE_URL.replace(/\/$/, '')}${path}`
  }
  return path
}

export async function getAvailablePermitTypes(tenantId: string): Promise<PermitType[]> {
  const response = await axios.get(buildUrl('/api/v1/template/permit-types'), {
    params: { tenantId },
  })

  return response.data ?? []
}

export async function getTemplates(tenantId: string): Promise<TemplateSummary[]> {
  const response = await axios.get<TemplateSummary[]>(buildUrl('/api/v1/template/templates'), {
    params: { tenantId },
  })

  return response.data ?? []
}

export async function createTemplate(payload: CreateTemplateInput): Promise<TemplateSummary> {
  const response = await axios.post<TemplateSummary>(buildUrl('/api/v1/template/templates'), payload)
  return response.data
}

export async function getTemplateConfiguration(
  tenantId: string,
  permitCode: string,
  approvalStateId?: string | null,
): Promise<TemplateConfiguration | null> {
  const params: Record<string, string> = {
    tenantId,
    permitCode,
  }

  if (approvalStateId && approvalStateId.trim().length > 0) {
    params.approvalStageId = approvalStateId
  }

  const response = await axios.get(buildUrl('/api/v1/template/configuration'), {
    params,
  })

  return response.data ?? null
}

export async function getTemplateConfigurationByVersion(
  tenantId: string,
  templateVersionId: string,
  approvalStateId?: string | null,
): Promise<TemplateConfiguration | null> {
  const params: Record<string, string> = {
    tenantId,
    templateVersionId,
  }

  if (approvalStateId && approvalStateId.trim().length > 0) {
    params.approvalStageId = approvalStateId
  }

  const response = await axios.get<TemplateConfiguration | null>(buildUrl('/api/v1/template/configuration/by-version'), {
    params,
  })

  return response.data ?? null
}

export interface DatasourceOptionsResponse {
  items: TemplateFieldDatasourceItem[]
  pageNumber: number
  pageSize: number
  totalItems: number
  totalPages: number
  displayColumns?: TemplateFieldDatasourceDisplayColumn[]
  searchableColumns?: string[]
}

const isGuidLike = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim())

const normalizeKeyLoose = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, '')

const resolveDatasourceItemLabel = (
  item: TemplateFieldDatasourceItem,
  displayColumns: TemplateFieldDatasourceDisplayColumn[] = [],
): string => {
  const rawValue = (item.value ?? '').trim()
  const rawLabel = (item.label ?? '').trim()

  if (rawLabel && rawLabel.toLowerCase() !== rawValue.toLowerCase() && !isGuidLike(rawLabel)) {
    return rawLabel
  }

  const columns = item.columns ?? {}
  const columnEntries = Object.entries(columns)
  if (columnEntries.length > 0) {
    const preferredKeys = displayColumns
      .map((column) => (column.key ?? column.field ?? '').trim())
      .filter((key) => key.length > 0)

    for (const preferredKey of preferredKeys) {
      const exactMatch = columnEntries.find(([key]) => key === preferredKey)
      const looseMatch =
        exactMatch ??
        columnEntries.find(([key]) => normalizeKeyLoose(key) === normalizeKeyLoose(preferredKey))
      const candidate = looseMatch?.[1]
      const candidateText = candidate === null || candidate === undefined ? '' : String(candidate).trim()
      if (
        candidateText &&
        candidateText.toLowerCase() !== rawValue.toLowerCase() &&
        !isGuidLike(candidateText)
      ) {
        return candidateText
      }
    }

    for (const [, columnValue] of columnEntries) {
      const candidateText = columnValue === null || columnValue === undefined ? '' : String(columnValue).trim()
      if (
        candidateText &&
        candidateText.toLowerCase() !== rawValue.toLowerCase() &&
        !isGuidLike(candidateText)
      ) {
        return candidateText
      }
    }
  }

  if (rawLabel) {
    return rawLabel
  }

  return rawValue
}

export async function getFieldDatasourceOptions(
  fieldId: string,
  parameters: Record<string, string | undefined>,
  searchTerm?: string,
  pageNumber = 1,
  pageSize = 100,
  selectedValues?: string[],
): Promise<DatasourceOptionsResponse> {
  const requestStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
  const guidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

  const normalizedSelectedValues =
    selectedValues?.reduce<string[]>((acc, rawValue) => {
      const value = typeof rawValue === 'string' ? rawValue.trim() : ''
      if (!value) {
        return acc
      }
      if (!acc.includes(value)) {
        acc.push(value)
      }
      if (guidPattern.test(value)) {
        const upper = value.toUpperCase()
        const lower = value.toLowerCase()
        if (!acc.includes(upper)) {
          acc.push(upper)
        }
        if (!acc.includes(lower)) {
          acc.push(lower)
        }
      }
      return acc
    }, []) ?? []

  const response = await axios
    .post<DatasourceOptionsResponse>(
      buildUrl('/api/v1/template/fields/datasource/options'),
      {
        fieldId,
        parameters,
        searchTerm,
        pageNumber,
        pageSize,
        ...(normalizedSelectedValues.length > 0 ? { selectedValues: normalizedSelectedValues } : {}),
      },
      {
        timeout: 15000,
      },
    )
    .finally(() => {
      if (process.env.NODE_ENV !== 'development') {
        return
      }
      const requestEndedAt = typeof performance !== 'undefined' ? performance.now() : Date.now()
      const durationMs = Math.round(requestEndedAt - requestStartedAt)
      if (durationMs >= 1200) {
        // eslint-disable-next-line no-console
        console.warn('[datasource-options] slow response', {
          fieldId,
          searchTerm,
          pageNumber,
          pageSize,
          durationMs,
        })
      }
    })

  const data = response.data ?? {
    items: [],
    pageNumber,
    pageSize,
    totalItems: 0,
    totalPages: 0,
  }

  const normalizedDisplayColumns = data.displayColumns ?? []
  const normalizedItems = (data.items ?? []).map((item) => ({
    ...item,
    label: resolveDatasourceItemLabel(item, normalizedDisplayColumns),
  }))

  return {
    items: normalizedItems,
    pageNumber: data.pageNumber ?? pageNumber,
    pageSize: data.pageSize ?? pageSize,
    totalItems: data.totalItems ?? normalizedItems.length,
    totalPages: data.totalPages ?? (data.pageSize ? Math.ceil(normalizedItems.length / data.pageSize) : 1),
    displayColumns: normalizedDisplayColumns,
    searchableColumns: data.searchableColumns ?? [],
  }
}

export async function updateTemplateConfiguration(
  payload: UpdateTemplateConfigurationInput,
): Promise<TemplateConfiguration | null> {
  const response = await axios.post<TemplateConfiguration | null>(
    buildUrl('/api/v1/template/configuration'),
    payload,
  )

  return response.data ?? null
}
